import { useEffect, useMemo, useRef, useState } from "react";
import { MemberMarker, VojomoLoader } from "@vojomo-core/shared";
import { IconTarget, IconTrendingUp } from "@tabler/icons-react";
import styles from "../QBR/QBRTable.module.css";
import { type ProgressRows, useWeeklyProgress } from "./use-weekly-progress";
import { InitialsAvatar } from "components/ObjectivesTable/ObjectivesTable";

// --- Week calculation utilities ---

function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getQuarterMondays(date: Date): Date[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const quarter = Math.floor(month / 3);
  const quarterStartMonth = quarter * 3;
  const quarterStart = new Date(year, quarterStartMonth, 1);
  const dayOfWeek = quarterStart.getDay();
  const daysToMonday =
    dayOfWeek === 1 ? 0 : dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const firstMonday = new Date(quarterStart);
  firstMonday.setDate(quarterStart.getDate() + daysToMonday);

  const mondays: Date[] = [];
  for (let i = 0; i < 13; i++) {
    const m = new Date(firstMonday);
    m.setDate(firstMonday.getDate() + i * 7);
    mondays.push(m);
  }
  return mondays;
}

function formatDateForApi(date: Date): string {
  return date.toISOString().split("T")[0];
}

function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// weekIndex is 0-based; expected progress at week N = (N+1)/13 * 100%
function getProgressColor(value: string, weekIndex: number): string {
  const parsed = Number(value);
  if (!value || Number.isNaN(parsed)) return "";
  const expected = ((weekIndex + 1) / 13) * 100;
  if (parsed >= expected) return styles.varianceGreen;
  if (parsed >= expected * 0.75) return styles.varianceYellow;
  if (parsed >= expected * 0.5) return styles.varianceOrange;
  return styles.varianceRed;
}

// --- Editable cell helpers ---

type EditField = "pct" | "notes" | "actions";

const EditButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    className={styles.rationaleEditButton}
    onClick={onClick}
    aria-label="Edit"
  >
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path
        d="M4 13.75V16h2.25l8.1-8.1-2.25-2.25L4 13.75Zm11.8-7.55a.6.6 0 0 0 0-.85l-1.15-1.15a.6.6 0 0 0-.85 0l-1.1 1.1 2.25 2.25 1.1-1.1Z"
        fill="currentColor"
      />
    </svg>
  </button>
);

// --- Component ---

export const WeeklyProgress = () => {
  const today = useMemo(() => new Date(), []);
  const quarterMondays = useMemo(() => getQuarterMondays(today), [today]);

  const currentMondayStr = useMemo(
    () => formatDateForApi(getMondayOfWeek(today)),
    [today]
  );

  const currentWeekIndex = useMemo(() => {
    const idx = quarterMondays.findIndex(
      (m) => formatDateForApi(m) === currentMondayStr
    );
    return idx >= 0 ? idx : quarterMondays.length - 1;
  }, [quarterMondays, currentMondayStr]);

  const [weekIndex, setWeekIndex] = useState(currentWeekIndex);
  const weekDateStr = formatDateForApi(quarterMondays[weekIndex]);
  const isCurrentWeek = weekIndex === currentWeekIndex;
  const isNextWeek = weekIndex === currentWeekIndex + 1;
  const maxWeekIndex = Math.min(currentWeekIndex + 1, quarterMondays.length - 1);

  const {
    isLoading,
    progressData,
    progressTable,
    progressRows,
    setProgressRows,
    getPlainText,
    saveProgress
  } = useWeeklyProgress(weekDateStr);

  const [editingCell, setEditingCell] = useState<{
    row: number;
    field: EditField;
  } | null>(null);

  const cellRefs = useRef<
    Record<string, HTMLTextAreaElement | HTMLInputElement | null>
  >({});

  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setEditingCell(null);
  }, [weekDateStr]);

  useEffect(() => {
    if (!editingCell) return;
    const key = `${editingCell.row}-${editingCell.field}`;
    const el = cellRefs.current[key];
    if (el) {
      el.focus();
      try {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch {
        // number inputs don't support setSelectionRange
      }
    }
  }, [editingCell]);

  const triggerSave = (rows: ProgressRows) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveProgress(rows), 1500);
  };

  const handleChange = (
    rowIndex: number,
    field: keyof ProgressRows[number],
    value: string
  ) => {
    const cur = progressRows[rowIndex] ?? {
      progress_pct: "",
      notes: "",
      actions: ""
    };
    const newRows: ProgressRows = {
      ...progressRows,
      [rowIndex]: { ...cur, [field]: value }
    };
    setProgressRows(newRows);
    triggerSave(newRows);
  };

  const weekLabel = formatDateForDisplay(quarterMondays[weekIndex]);
  const aHeader = progressTable?.headers[2] ?? "A";

  if (isLoading) return <VojomoLoader />;

  if (!progressTable?.hasTable) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-[#7C788A]">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-32 w-32 items-center justify-center rounded-full border border-[#E6E3EE]">
            <IconTarget size={56} className="text-[#7C788A]" />
          </div>
          <MemberMarker />
          <p>
            No Quarterly Objectives set yet. Add them in the Roadmap section
            first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="px-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-semibold text-[#4B4758]">
            Weekly Progress
          </h2>
          <MemberMarker />
        </div>
      </div>

      {/* Week navigation bar */}
      <div className="mx-6 rounded-2xl border border-[#E6E3EE] bg-white p-4 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <IconTrendingUp size={20} className="text-[#9A96A8]" />
          <span className="text-sm font-semibold text-[#4B4758]">
            Week of {weekLabel}
          </span>
          {isCurrentWeek && (
            <span className="rounded-full bg-[#E9E7F3] px-3 py-0.5 text-xs font-semibold text-[#5E5A6E]">
              Current Week
            </span>
          )}
          {isNextWeek && (
            <span className="rounded-full bg-[#E3EEF9] px-3 py-0.5 text-xs font-semibold text-[#3B6EA5]">
              Next Week
            </span>
          )}
          {progressData?.last_updated_by_name && (
            <span className="text-xs text-[#9A96A8]">
              Last updated by {progressData.last_updated_by_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-[#9A96A8]">
            Week {weekIndex + 1} of {quarterMondays.length}
          </span>
          <button
            disabled={weekIndex === 0}
            onClick={() => setWeekIndex((w) => w - 1)}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#E6E3EE] bg-white text-[#6B657A] text-lg leading-none transition hover:bg-[#F2F1F7] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous week"
          >
            ‹
          </button>
          <button
            disabled={weekIndex >= maxWeekIndex}
            onClick={() => setWeekIndex((w) => w + 1)}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#E6E3EE] bg-white text-[#6B657A] text-lg leading-none transition hover:bg-[#F2F1F7] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next week"
          >
            ›
          </button>
        </div>
      </div>

      {/* Progress legend */}
      {(() => {
        const expected = ((weekIndex + 1) / 13) * 100;
        const fmt = (n: number) => `${Math.round(n)}%`;
        return (
          <div className="mx-6 rounded-2xl border border-[#E6E3EE] bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-[280px] flex-1">
                <div className="text-sm font-semibold text-[#4B4758]">
                  Progress Legend — Week {weekIndex + 1} Targets
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#7C788A]">
                  <div className="flex items-center gap-3">
                    <span className="rounded-lg bg-[#2FB764] px-3 py-1 text-sm font-semibold text-[#FFFFFF]">
                      On Track
                    </span>
                    <span>{fmt(expected)}+</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-lg bg-[#F3C623] px-3 py-1 text-sm font-semibold text-[#000000]">
                      Behind
                    </span>
                    <span>{fmt(expected * 0.75)}+</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-lg bg-[#D66E2E] px-3 py-1 text-sm font-semibold text-[#FFFFFF]">
                      Far Behind
                    </span>
                    <span>{fmt(expected * 0.5)}+</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-lg bg-[#E34A4A] px-3 py-1 text-sm font-semibold text-[#FFFFFF]">
                      Red Flag
                    </span>
                    <span>&lt; {fmt(expected * 0.5)}</span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-[#7C788A]">
                  Thresholds adjust each week to reflect expected quarterly pace. By week {weekIndex + 1} of 13, progress should be at {fmt(expected)}.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Progress grid */}
      <div className={`mx-6 overflow-x-auto ${styles.qbrWrapper}`}>
        <table
          className={`w-full text-sm ${styles.qbrTable}`}
          style={{ tableLayout: "fixed" }}
        >
          <colgroup>
            <col style={{ width: "22%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "17.5%" }} />
            <col style={{ width: "17.5%" }} />
          </colgroup>
          <thead>
            {/* Row 1: static cols span both rows; "Week of …" spans the 3 editable cols */}
            <tr>
              <th
                rowSpan={2}
                className={styles.qbrHeaderCell}
                style={{ verticalAlign: "middle" }}
              >
                {progressTable.headers[0] ?? "Objective"}
              </th>
              <th
                rowSpan={2}
                className={styles.qbrHeaderCell}
                style={{ verticalAlign: "middle" }}
              >
                {progressTable.headers[1] ?? "Measurements"}
              </th>
              <th
                rowSpan={2}
                className={styles.qbrHeaderCell}
                style={{ verticalAlign: "middle" }}
                dangerouslySetInnerHTML={{ __html: aHeader }}
              />
              <th
                colSpan={3}
                className={styles.qbrHeaderCell}
                style={{
                  textAlign: "center",
                  borderBottom: "1px solid #C8C3D4",
                  color: "#9A96A8",
                  fontWeight: 500,
                  fontSize: 11,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase"
                }}
              >
                Week of {weekLabel}
              </th>
            </tr>
            {/* Row 2: sub-headers for the 3 editable cols */}
            <tr>
              <th
                className={`${styles.qbrHeaderCell} ${styles.qbrHeaderCellCenter}`}
              >
                % Progress
              </th>
              <th className={styles.qbrHeaderCell}>Progress Notes</th>
              <th className={styles.qbrHeaderCell}>Progress Actions</th>
            </tr>
          </thead>
          <tbody>
            {progressTable.rows.map((row, rowIndex) => {
              const cur = progressRows[rowIndex];
              return (
                <tr key={`wp-r-${rowIndex}`}>
                  {/* Objective — plain text, no pill */}
                  <td className={styles.qbrCell}>
                    <span dangerouslySetInnerHTML={{ __html: row[0] ?? "" }} />
                  </td>

                  {/* Measurements */}
                  <td className={styles.qbrCell}>
                    <span dangerouslySetInnerHTML={{ __html: row[1] ?? "" }} />
                  </td>

                  {/* A / Accountable — read-only from objectives table */}
                  <td className={`${styles.qbrCell} ${styles.qbrCellCenter}`}>
                    {(() => {
                      const name = getPlainText(row[2] ?? "");
                      return name ? (
                        <InitialsAvatar name={name} />
                      ) : (
                        <span style={{ color: "#8B869A" }}>–</span>
                      );
                    })()}
                  </td>

                  {/* % Progress — click-to-edit */}
                  <td className={`${styles.qbrCell} ${styles.rationaleCell}`}>
                    {editingCell?.row === rowIndex &&
                    editingCell.field === "pct" ? (
                      <div className={styles.scoreCell}>
                        <input
                          ref={(el) => {
                            cellRefs.current[`${rowIndex}-pct`] = el;
                          }}
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          onWheel={(e) => e.currentTarget.blur()}
                          value={cur?.progress_pct ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              handleChange(rowIndex, "progress_pct", "");
                              return;
                            }
                            const clamped = Math.min(100, Math.max(0, Number(raw)));
                            handleChange(rowIndex, "progress_pct", String(clamped));
                          }}
                          onBlur={() => setEditingCell(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setEditingCell(null);
                          }}
                          className={styles.scoreInput}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${styles.rationaleDisplay} ${!cur?.progress_pct ? styles.rationaleEmpty : ""}`}
                        style={{ justifyContent: "center" }}
                        onDoubleClick={() =>
                          setEditingCell({ row: rowIndex, field: "pct" })
                        }
                      >
                        {cur?.progress_pct ? (
                          <span
                            className={`${styles.scoreBadge} ${getProgressColor(cur.progress_pct, weekIndex)}`}
                          >
                            {cur.progress_pct}%
                          </span>
                        ) : (
                          <span>-</span>
                        )}
                        <EditButton
                          onClick={() =>
                            setEditingCell({ row: rowIndex, field: "pct" })
                          }
                        />
                      </div>
                    )}
                  </td>

                  {/* Progress Notes */}
                  <td className={`${styles.qbrCell} ${styles.rationaleCell}`}>
                    {editingCell?.row === rowIndex &&
                    editingCell.field === "notes" ? (
                      <textarea
                        ref={(el) => {
                          cellRefs.current[`${rowIndex}-notes`] = el;
                        }}
                        className={styles.rationaleInput}
                        rows={2}
                        value={cur?.notes ?? ""}
                        onChange={(e) =>
                          handleChange(rowIndex, "notes", e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            setEditingCell(null);
                          }
                        }}
                        onBlur={() => setEditingCell(null)}
                      />
                    ) : (
                      <div
                        className={`${styles.rationaleDisplay} ${!cur?.notes?.trim() ? styles.rationaleEmpty : ""}`}
                        onDoubleClick={() =>
                          setEditingCell({ row: rowIndex, field: "notes" })
                        }
                      >
                        <span>{cur?.notes?.trim() || "-"}</span>
                        <EditButton
                          onClick={() =>
                            setEditingCell({ row: rowIndex, field: "notes" })
                          }
                        />
                      </div>
                    )}
                  </td>

                  {/* Progress Actions */}
                  <td className={`${styles.qbrCell} ${styles.rationaleCell}`}>
                    {editingCell?.row === rowIndex &&
                    editingCell.field === "actions" ? (
                      <textarea
                        ref={(el) => {
                          cellRefs.current[`${rowIndex}-actions`] = el;
                        }}
                        className={styles.rationaleInput}
                        rows={2}
                        value={cur?.actions ?? ""}
                        onChange={(e) =>
                          handleChange(rowIndex, "actions", e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            setEditingCell(null);
                          }
                        }}
                        onBlur={() => setEditingCell(null)}
                      />
                    ) : (
                      <div
                        className={`${styles.rationaleDisplay} ${!cur?.actions?.trim() ? styles.rationaleEmpty : ""}`}
                        onDoubleClick={() =>
                          setEditingCell({ row: rowIndex, field: "actions" })
                        }
                      >
                        <span>{cur?.actions?.trim() || "-"}</span>
                        <EditButton
                          onClick={() =>
                            setEditingCell({ row: rowIndex, field: "actions" })
                          }
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
